import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import TemuRefreshButton from '@/temu-platform/components/TemuRefreshButton';
import TemuPageToolbar, { downloadCsv } from '@/temu-platform/modules/products/components/TemuPageToolbar';
import { useTemuManualSyncQuota } from '@/temu-platform/hooks/useTemuManualSyncQuota';
import { toTemuManualSyncQuotaKind } from '@/utils/temuManualSyncQuotaKind';
import '@/styles/temu-platform/temu-ops-list-toolbar.css';

export { downloadCsv };

const SYNC_HINT_EDGE = 8;
const SYNC_HINT_MAX_WIDTH = 280;

/** 同步按钮内问号：自定义悬浮说明，靠右对齐并支持换行，避免溢出视口右边界 */
export function SyncHintMark({ text }) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxWidth = Math.min(SYNC_HINT_MAX_WIDTH, Math.max(160, window.innerWidth - SYNC_HINT_EDGE * 2));
    // 按钮在工具栏最右：优先右对齐触发点，避免 tooltip 溢出右边
    let left = rect.right - maxWidth;
    if (left < SYNC_HINT_EDGE) left = SYNC_HINT_EDGE;
    if (left + maxWidth > window.innerWidth - SYNC_HINT_EDGE) {
      left = Math.max(SYNC_HINT_EDGE, window.innerWidth - maxWidth - SYNC_HINT_EDGE);
    }
    const tipHeight = tipRef.current?.offsetHeight || 72;
    const belowTop = rect.bottom + 6;
    const top = belowTop + tipHeight > window.innerHeight - SYNC_HINT_EDGE
      ? Math.max(SYNC_HINT_EDGE, rect.top - tipHeight - 6)
      : belowTop;
    setPos({ top, left, maxWidth });
  }, []);

  const show = useCallback(() => {
    updatePos();
    setOpen(true);
  }, [updatePos]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updatePos();
    const raf = window.requestAnimationFrame(() => updatePos());
    const onScrollOrResize = () => updatePos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePos]);

  if (!text) return null;

  return (
    <>
      <span
        ref={triggerRef}
        className="tpo-ops-list-toolbar__sync-hint"
        aria-label={text}
        role="img"
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        ?
      </span>
      {open && pos && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={tipRef}
            className="tpo-ops-list-toolbar__sync-hint-tip"
            style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth }}
            role="tooltip"
          >
            {text}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}

/**
 * TEMU 列表统一操作栏（复选框页面复用）。
 *
 * 布局：导出在最左；更新时间与拉取 TEMU 后台数据在最右；中间为批量等操作。
 * 平台 JSON 下载入口已隐藏，不再展示。
 *
 * `batchOpsMenu`：聚合「批量操作」下拉（复制/移动二级菜单 + 批量删除）；
 * 传入后不再单独渲染 batchCopyMenu / batchMoveMenu / onBatchDelete。
 */
export default function TemuOpsListToolbar({
  selectedCount = 0,
  busy = false,
  batchCopying = false,
  batchCopyingLabel = '',
  batchMoving = false,
  batchMovingLabel = '',
  syncKinds,
  onSync,
  syncing = false,
  syncCooldownSeconds = 0,
  syncDisabled = false,
  lastSyncedLabel = '',
  /** 同步按钮文案（默认「拉取 TEMU 后台数据」，各页可覆盖） */
  syncLabel = '拉取 TEMU 后台数据',
  /** 拉取按钮内问号的悬浮说明（可换行，防右溢出） */
  syncHint = '',
  onExport,
  exportLabel = '导出数据',
  exportMenu,
  exportDisabled = false,
  onBatchDelete,
  deleteLabel = fixedUi("批量删除"),
  deleteDisabled = false,
  batchCopyMenu = [],
  batchMoveMenu = [],
  batchOpsMenu = [],
  batchOpsLabel = '批量操作',
  extraActions = [],
  /** 右侧（拉取按钮旁）追加的自定义节点，如「平台 JSON」按钮 */
  trailingExtra = null,
  actions: customActions,
  quotaKind,
}) {
  const { t } = useTranslation('workbench');
  const resolvedExportLabel = fixedUi(exportLabel || '') || t('tiktokList.exportData');
  const resolvedDeleteLabel = fixedUi(deleteLabel || '') || t('tiktokList.batchDelete');
  const resolvedBatchOpsLabel = fixedUi(batchOpsLabel || '') || t('tiktokList.batchOps');
  const kindList = useMemo(
    () => (Array.isArray(syncKinds) ? syncKinds : (syncKinds ? [syncKinds] : [])).filter(Boolean).map(String),
    [syncKinds],
  );
  const showSync = kindList.length > 0 && typeof onSync === 'function';
  const quotaButtonKind = showSync
    ? (quotaKind || toTemuManualSyncQuotaKind(kindList[0]))
    : '';
  const quota = useTemuManualSyncQuota(quotaButtonKind);
  const quotaExhausted = showSync && quota.exhausted;
  const handleSyncClick = () => {
    if (!quota.assertCanSync()) return;
    onSync?.();
  };

  const actions = useMemo(() => {
    if (Array.isArray(customActions)) return customActions;

    const list = [];
    // 导出：工具栏最左
    if (Array.isArray(exportMenu) && exportMenu.length) {
      list.push({
        key: 'export',
        label: resolvedExportLabel,
        disabled: exportDisabled,
        menu: exportMenu,
      });
    } else if (typeof onExport === 'function') {
      list.push({
        key: 'export',
        label: resolvedExportLabel,
        disabled: exportDisabled,
        onClick: onExport,
      });
    }
    if (Array.isArray(extraActions) && extraActions.length) {
      extraActions.forEach((item) => {
        if (
          item?.key
          && item?.label
          && item.key !== 'platform-json'
          && item.key !== 'download-platform-json'
          && !String(item.key).startsWith('platform-json-')
        ) {
          list.push({ ...item, label: fixedUi(item.label) });
        }
      });
    }

    const useBatchOps = Array.isArray(batchOpsMenu) && batchOpsMenu.length > 0;
    if (useBatchOps) {
      list.push({
        key: 'batch-ops',
        label: selectedCount > 0 ? `${resolvedBatchOpsLabel}(${selectedCount})` : resolvedBatchOpsLabel,
        disabled: selectedCount <= 0,
        loading: batchCopying || batchMoving,
        loadingLabel: batchCopying
          ? (batchCopyingLabel || (selectedCount > 0 ? t('tiktokList.copyingCount', { count: selectedCount }) : t('tiktokList.copying')))
          : (batchMovingLabel || (selectedCount > 0 ? t('tiktokList.movingCount', { count: selectedCount }) : t('tiktokList.moving'))),
        menu: batchOpsMenu,
      });
    } else {
      if (Array.isArray(batchCopyMenu) && batchCopyMenu.length) {
        list.push({
          key: 'batch-copy',
          label: selectedCount > 0 ? t('tiktokList.batchCopyCount', { count: selectedCount }) : t('tiktokList.batchCopy'),
          disabled: selectedCount <= 0,
          loading: batchCopying,
          loadingLabel: batchCopyingLabel || (selectedCount > 0 ? t('tiktokList.copyingCount', { count: selectedCount }) : t('tiktokList.copying')),
          menu: batchCopyMenu,
        });
      }
      if (Array.isArray(batchMoveMenu) && batchMoveMenu.length) {
        list.push({
          key: 'batch-move',
          label: selectedCount > 0 ? t('tiktokList.batchMoveCount', { count: selectedCount }) : t('tiktokList.batchMove'),
          disabled: selectedCount <= 0,
          loading: batchMoving,
          loadingLabel: batchMovingLabel || (selectedCount > 0 ? t('tiktokList.movingCount', { count: selectedCount }) : t('tiktokList.moving')),
          menu: batchMoveMenu,
        });
      }
      if (typeof onBatchDelete === 'function') {
        list.push({
          key: 'delete',
          label: selectedCount > 0 ? `${resolvedDeleteLabel}(${selectedCount})` : resolvedDeleteLabel,
          tone: 'danger',
          disabled: deleteDisabled || selectedCount <= 0,
          onClick: onBatchDelete,
        });
      }
    }
    return list;
  }, [
    customActions,
    exportMenu,
    resolvedExportLabel,
    exportDisabled,
    onExport,
    extraActions,
    onBatchDelete,
    resolvedDeleteLabel,
    deleteDisabled,
    batchCopyMenu,
    batchMoveMenu,
    batchOpsMenu,
    resolvedBatchOpsLabel,
    t,
    selectedCount,
    batchCopying,
    batchCopyingLabel,
    batchMoving,
    batchMovingLabel,
  ]);

  const trailing = (showSync || trailingExtra) ? (
    <div className="tpo-ops-list-toolbar__trailing-group">
      {showSync ? (
      <div className="tpo-ops-list-toolbar__sync">
        {lastSyncedLabel ? (
          <span className="tpo-sync-updated-at" title={lastSyncedLabel}>
            {lastSyncedLabel}
          </span>
        ) : null}
        {quota.remaining != null ? (
          <span
            className="tpo-sync-quota"
            title={`当前店铺该同步每天 ${quota.limit} 次；一次点击成功只扣 1 次，其它店铺不占用`}
          >
            今日剩余 {quota.remaining}/{quota.limit}
          </span>
        ) : null}
        <span className={`tpo-ops-list-toolbar__sync-action${syncHint ? ' tpo-ops-list-toolbar__sync-action--with-hint' : ''}`}>
          <SyncHintMark text={syncHint} />
          <TemuRefreshButton
            className={`tpo-btn tpo-btn--secondary tpo-btn--sm tpo-ops-list-toolbar__sync-btn${syncHint ? ' tpo-ops-list-toolbar__sync-btn--with-hint' : ''}`}
            onClick={handleSyncClick}
            loading={syncing}
            cooldownSeconds={syncCooldownSeconds}
            busyLabel={fixedUi("同步中…")}
            cooldownLabel={(s) => `${s}s 后可同步`}
            disabled={busy || syncDisabled || syncing || quotaExhausted}
          >
            {quotaExhausted ? '今日已达上限' : syncLabel}
          </TemuRefreshButton>
        </span>
      </div>
      ) : null}
      {trailingExtra}
    </div>
  ) : null;

  if (!trailing && (!Array.isArray(actions) || actions.length === 0)) {
    return null;
  }

  return (
    <div className="tpo-ops-list-toolbar">
      <TemuPageToolbar
        trailing={trailing}
        actions={actions}
        busy={busy}
      />
    </div>
  );
}
